import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { useFormik } from 'formik';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import MultiSelect from '../../components/MultiSelect';
import FileUpload from '../../components/FileUpload';
import FilesList from '../../components/FilesList';
import Button from '../../components/Button';
import DateTimeInput from '../../components/DateTimeInput';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { ActivityFeedViewTypes, ViewTypes } from './ActivityFeedModal';
import { DashboardActivityFeedCommand } from '../../api/models';

const UpdateActivity: React.FC<ActivityFeedViewTypes> = ({
	activity = {},
	showView,
	closeModal,
	refreshActivityFeed
}) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const activityFeedSharedWith = useAppSelector((state) => state.lists.activityFeedSharedWith);

	const childSafeStandards = useAppSelector((state) => state.lists.childSafeStandards);

	const {
		id = '',
		title = '',
		date = '',
		description = '',
		standards = [],
		attachments = [],
		sharedWith = 'Internal'
	} = activity;

	const createAttachments: string[] = [];
	const deleteAttachmentIds: string[] = [];

	const mode = !id ? 'create' : 'update';

	const updateActivityFeed = async (values: DashboardActivityFeedCommand) => {
		if (values !== undefined && values.id !== undefined) {
			setIsLoading(true);

			const response = await apibridge.updateActivityFeed(values);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				refreshActivityFeed();

				if (closeModal) {
					closeModal();
				}
			}
		}
	};

	const initialValues: DashboardActivityFeedCommand = {
		id,
		title,
		date,
		description,
		standards,
		createAttachments,
		deleteAttachmentIds,
		sharedWith
	};

	const formik = useFormik({
		initialValues,
		validate: (values) => {
			const errors: any = {};

			if (!values.title) {
				errors.title = 'Required';
			}

			if (!values.description) {
				errors.description = 'Required';
			}

			if (!values.sharedWith) {
				errors.sharedWith = 'Required';
			}

			return errors;
		},
		onSubmit: (values) => {
			updateActivityFeed(values);
		}
	});

	return (
		<form onSubmit={formik.handleSubmit}>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="form-group">
									<label htmlFor="title">Title of Activity</label>
									<input
										className="form-control"
										name="title"
										id="title"
										type="text"
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.title || ''}
										placeholder="e.g. Information session on Respectful Relationships"
									/>
									{formik.touched.title && formik.errors.title ? (
										<div className="form-validation-error">{formik.errors.title}</div>
									) : null}
								</div>

								<div className="form-group">
									<label htmlFor="date">Date and time of the activity</label>
									<DateTimeInput
										dateTimeString={formik.values.date || ''}
										onChange={(val: string) => formik.setFieldValue('date', val)}
									/>
								</div>

								<div className="form-group">
									<label htmlFor="standards">Child Safe Standard(s) activity supports</label>
									<MultiSelect
										name="standards"
										placeholder="Select all that apply..."
										values={standards}
										options={childSafeStandards}
										onChange={(vals: string[]) => formik.setFieldValue('standards', vals)}
									></MultiSelect>
								</div>

								<div className="form-group">
									<label htmlFor="createAttachments">
										Attachments <span className="note">(optional)</span>
									</label>

									<FilesList
										files={attachments || []}
										onChange={(vals: string[]) => formik.setFieldValue('deleteAttachmentIds', vals)}
										multiple={true}
									/>

									<FileUpload
										name="createAttachments"
										onChange={(vals: string[]) => formik.setFieldValue('createAttachments', vals)}
										multiple={true}
										maxSizeMb={5}
									></FileUpload>
								</div>

								<div className="form-group">
									<label htmlFor="description">Description of activity</label>
									<textarea
										className="form-control"
										name="description"
										id="description"
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.description}
									></textarea>
									{formik.touched.description && formik.errors.description ? (
										<div className="form-validation-error">{formik.errors.description}</div>
									) : null}
								</div>

								<div className="form-group">
									<label htmlFor="shared">Who would you like this activity to be shared with?</label>

									{activityFeedSharedWith &&
										activityFeedSharedWith.length > 0 &&
										activityFeedSharedWith.map((item) => (
											<label className="form-control-radio" key={'shared-' + item.value}>
												<input
													id="sharedWith"
													name="sharedWith"
													type="radio"
													value={item.value}
													checked={formik.values.sharedWith === item.value}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
												/>
												<span>{item.label}</span>
											</label>
										))}

									{formik.touched.sharedWith && formik.errors.sharedWith ? (
										<div className="form-validation-error">{formik.errors.sharedWith}</div>
									) : null}
								</div>

								<section className="actions">
									<Button className="btn-primary full-width" type="submit">
										{mode === 'update' ? <>Update</> : <>Log an activity</>}
									</Button>

									{mode === 'update' && (
										<Button className="btn-secondary full-width" onClick={() => showView(ViewTypes.Read)}>
											Cancel
										</Button>
									)}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</form>
	);
};

export default UpdateActivity;
